import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'react-hot-toast';
import { Calculator, DollarSign, ArrowRight, Package, User, Loader2, AlertCircle } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PricingSimulator = () => {
  const [pricingProcedures, setPricingProcedures] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isCalculating, setIsCalculating] = useState(false);
  const [pricingResult, setPricingResult] = useState(null);

  const [formData, setFormData] = useState({
    pricing_procedure_id: 'ZSTD01',
    material_id: '',
    quantity: 1,
    unit_of_measure: 'EA',
    customer_id: '',
    sales_org: '',
    distribution_channel: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setIsLoading(true);
    try {
      const [procRes, matsRes, custRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/pricing/pricing-procedures`),
        axios.get(`${BACKEND_URL}/api/materials`).catch(() => ({ data: [] })),
        axios.get(`${BACKEND_URL}/api/customers`).catch(() => ({ data: [] }))
      ]);
      setPricingProcedures(procRes.data || []);
      setMaterials(matsRes.data || []);
      setCustomers(custRes.data || []);
    } catch (error) {
      console.error('Error loading master data:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Unknown error';
      toast.error(`Failed to load master data: ${errorMsg}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCalculate = async () => {
    if (!formData.pricing_procedure_id) {
      toast.error('Please select a pricing procedure');
      return;
    }
    if (!formData.material_id) {
      toast.error('Please select a material');
      return;
    }
    if (!formData.quantity || formData.quantity <= 0) {
      toast.error('Quantity must be greater than 0');
      return;
    }

    setIsCalculating(true);
    setPricingResult(null);
    try {
      const params = new URLSearchParams({
        pricing_procedure_id: formData.pricing_procedure_id,
        material_id: formData.material_id,
        quantity: formData.quantity,
        unit_of_measure: formData.unit_of_measure
      });
      
      if (formData.customer_id) params.append('customer_id', formData.customer_id);
      if (formData.sales_org) params.append('sales_org', formData.sales_org);
      if (formData.distribution_channel) params.append('distribution_channel', formData.distribution_channel);

      const response = await axios.post(`${BACKEND_URL}/api/pricing/calculate?${params.toString()}`);
      setPricingResult(response.data);
      
      const material = materials.find(m => m.material_number === formData.material_id);
      const materialName = material?.description || formData.material_id;
      toast.success(`Pricing calculated for ${materialName}: ${response.data.currency} ${response.data.gross_price.toFixed(2)}`);
    } catch (error) {
      console.error('Error calculating pricing:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Unknown error';
      toast.error(`Failed to calculate pricing: ${errorMsg}`);
    } finally {
      setIsCalculating(false);
    }
  };

  const getLineTypeColor = (conditionType) => {
    if (conditionType.startsWith('PR')) return 'bg-orange-50 border-orange-200';
    if (conditionType.startsWith('K')) return 'bg-green-50 border-green-200';
    if (conditionType === 'MWST') return 'bg-red-50 border-red-200';
    if (conditionType === 'KF00') return 'bg-orange-50 border-orange-200';
    return 'bg-gray-50 border-gray-200';
  };

  return (
    <Layout>
      <div className="p-6">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Pricing Simulator</h1>
          <p className="text-sm text-gray-600 mt-1">Test pricing calculation with different scenarios</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Input Form */}
          <Card className="p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Calculator className="w-5 h-5" />
              Pricing Parameters
            </h2>

            <div className="space-y-4">
              <div>
                <Label>Pricing Procedure *</Label>
                <Select 
                  value={formData.pricing_procedure_id} 
                  onValueChange={(v) => setFormData({...formData, pricing_procedure_id: v})}
                >
                  <SelectTrigger><SelectValue placeholder="Select Procedure" /></SelectTrigger>
                  <SelectContent>
                    {pricingProcedures.map(proc => (
                      <SelectItem key={proc.pricing_procedure_id} value={proc.pricing_procedure_id}>
                        {proc.pricing_procedure_id} - {proc.description}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="flex items-center gap-2">
                  <Package className="w-4 h-4" />
                  Material *
                </Label>
                <Select 
                  value={formData.material_id || '__SELECT__'} 
                  onValueChange={(v) => setFormData({...formData, material_id: v === '__SELECT__' ? '' : v})}
                >
                  <SelectTrigger><SelectValue placeholder="Select Material" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__SELECT__">Select Material</SelectItem>
                    {materials.map(m => (
                      <SelectItem key={m.material_number} value={m.material_number}>
                        {m.material_number} - {m.description}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Quantity *</Label>
                  <Input
                    type="number"
                    step="0.001"
                    min="0.001"
                    value={formData.quantity}
                    onChange={(e) => setFormData({...formData, quantity: parseFloat(e.target.value) || 0})}
                    placeholder="1"
                  />
                </div>
                <div>
                  <Label>Unit of Measure</Label>
                  <Select value={formData.unit_of_measure} onValueChange={(v) => setFormData({...formData, unit_of_measure: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="EA">EA</SelectItem>
                      <SelectItem value="PC">PC</SelectItem>
                      <SelectItem value="KG">KG</SelectItem>
                      <SelectItem value="L">L</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label className="flex items-center gap-2">
                  <User className="w-4 h-4" />
                  Customer (Optional)
                </Label>
                <Select 
                  value={formData.customer_id || '__NONE__'} 
                  onValueChange={(v) => setFormData({...formData, customer_id: v === '__NONE__' ? '' : v})}
                >
                  <SelectTrigger><SelectValue placeholder="Select Customer" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__NONE__">-- No Customer --</SelectItem>
                    {customers.map(c => (
                      <SelectItem key={c.customer_id} value={c.customer_id}>
                        {c.customer_id} - {c.customer_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-500 mt-1">Customer-specific discounts will be applied if available</p>
              </div>

              <Button 
                onClick={handleCalculate} 
                className="w-full mt-4"
                disabled={isCalculating || !formData.material_id || !formData.quantity}
                data-testid="calculate-pricing-btn"
              >
                {isCalculating ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Calculating pricing...
                  </>
                ) : (
                  <>
                    <Calculator className="w-4 h-4 mr-2" />
                    Calculate Pricing
                  </>
                )}
              </Button>
            </div>
          </Card>

          {/* Results */}
          <Card className="p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <DollarSign className="w-5 h-5" />
              Pricing Result
            </h2>

            {isCalculating ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-orange-600" />
                <span className="ml-3 text-gray-600">Calculating pricing...</span>
              </div>
            ) : !pricingResult ? (
              <div className="text-center py-12">
                <Calculator className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p className="text-gray-500">Enter parameters and click "Calculate Pricing" to see results</p>
                <p className="text-sm text-gray-400 mt-1">Select a material, enter quantity, and optionally select a customer</p>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Summary Header */}
                <div className="bg-gradient-to-r from-orange-600 to-orange-700 rounded-lg p-4 text-white">
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="text-sm opacity-80">Material: {pricingResult.material_id}</p>
                      <p className="text-sm opacity-80">Qty: {pricingResult.quantity} {pricingResult.unit_of_measure}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm opacity-80">Total Price</p>
                      <p className="text-3xl font-bold">{pricingResult.currency} {pricingResult.gross_price.toFixed(2)}</p>
                    </div>
                  </div>
                </div>

                {/* Pricing Lines */}
                <div className="space-y-2">
                  {pricingResult.pricing_lines.map((line) => (
                    <div
                      key={`${line.step_number}-${line.condition_type_id}`}
                      className={`p-3 rounded-lg border ${getLineTypeColor(line.condition_type_id)} ${line.is_statistical ? 'opacity-60' : ''}`}
                    >
                      <div className="flex justify-between items-center">
                        <div className="flex items-center gap-3">
                          <span className="font-mono font-bold text-sm bg-white px-2 py-1 rounded">
                            {line.step_number}
                          </span>
                          <div>
                            <span className="font-bold">{line.condition_type_id}</span>
                            <span className="text-gray-600 text-sm ml-2">{line.description}</span>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-xs text-gray-500">
                            {line.condition_type_id === 'PR00' || line.condition_type_id.startsWith('KF') 
                              ? `${line.currency} ${line.condition_rate}/unit` 
                              : `${line.condition_rate}%`}
                          </p>
                          <p className={`font-bold ${line.condition_value < 0 ? 'text-red-600' : 'text-green-600'}`}>
                            {line.condition_value >= 0 ? '+' : ''}{line.currency} {line.condition_value.toFixed(2)}
                          </p>
                        </div>
                      </div>
                      {line.is_statistical && (
                        <p className="text-xs text-gray-500 mt-1 italic">* Statistical - not included in total</p>
                      )}
                    </div>
                  ))}
                </div>

                {/* Totals */}
                <div className="border-t pt-4 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>Net Price:</span>
                    <span className="font-bold">{pricingResult.currency} {pricingResult.net_price.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span>Tax:</span>
                    <span className="font-bold text-red-600">+ {pricingResult.currency} {pricingResult.tax_amount.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-lg font-bold pt-2 border-t">
                    <span>Gross Price:</span>
                    <span className="text-orange-700">{pricingResult.currency} {pricingResult.gross_price.toFixed(2)}</span>
                  </div>
                </div>
              </div>
            )}
          </Card>
        </div>

        {/* Pricing Flow Diagram */}
        <Card className="p-6 mt-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Pricing Calculation Flow</h2>
          <div className="flex items-center justify-center gap-2 flex-wrap">
            <div className="bg-orange-100 px-4 py-2 rounded-lg text-orange-800 font-medium">
              PR00 (Base Price)
            </div>
            <ArrowRight className="w-5 h-5 text-gray-400" />
            <div className="bg-green-100 px-4 py-2 rounded-lg text-green-800 font-medium">
              K004 (Material Disc.)
            </div>
            <ArrowRight className="w-5 h-5 text-gray-400" />
            <div className="bg-green-100 px-4 py-2 rounded-lg text-green-800 font-medium">
              K005 (Customer Disc.)
            </div>
            <ArrowRight className="w-5 h-5 text-gray-400" />
            <div className="bg-orange-100 px-4 py-2 rounded-lg text-orange-800 font-medium">
              KF00 (Freight)
            </div>
            <ArrowRight className="w-5 h-5 text-gray-400" />
            <div className="bg-orange-100 px-4 py-2 rounded-lg text-orange-800 font-medium">
              = Net Price
            </div>
            <ArrowRight className="w-5 h-5 text-gray-400" />
            <div className="bg-red-100 px-4 py-2 rounded-lg text-red-800 font-medium">
              MWST (Tax)
            </div>
            <ArrowRight className="w-5 h-5 text-gray-400" />
            <div className="bg-gray-800 px-4 py-2 rounded-lg text-white font-bold">
              Gross Price
            </div>
          </div>
        </Card>
      </div>
    </Layout>
  );
};

export default PricingSimulator;
